import React, { useEffect, useState } from 'react'
import { Navigate, Route, BrowserRouter as Router, Routes, useLocation, useNavigate } from 'react-router-dom'
import { UsersAndGroups } from './components/Admin/UsersAndGroups'
import { ApiDocumentation } from './components/ApiDocumentation/ApiDocumentation'
import { AppFrame } from './components/AppFrame/AppFrame'
import { CorrespondentList } from './components/Correspondents/CorrespondentList'
import { CustomFields } from './components/CustomFields/CustomFields'
import { Dashboard } from './components/Dashboard/Dashboard'
import DocumentASN from './components/DocumentASN/DocumentASN'
import DocumentDetail from './components/DocumentDetail/DocumentDetail'
import DocumentList from './components/DocumentList/DocumentList'
import DocumentPreview from './components/DocumentPreview/DocumentPreview'
import { DocumentWorkflowDefinitionList } from './components/DocumentWorkflowDefinitions/DocumentWorkflowDefinitionList'
import { DocumentWorkflowTaskList } from './components/DocumentWorkflowTasks/DocumentWorkflowTaskList'
import { DocumentTypeList } from './components/DocumentTypes/DocumentTypeList'
import { WorkflowStatusList } from './components/WorkflowStatuses/WorkflowStatusList'
import { EmailSettings } from './components/Email/EmailSettings'
import { Login } from './components/Login/Login'
import { PrivateRoute } from './components/PrivateRoute'
import { ProtectedRoute } from './components/ProtectedRoute'
import { SavedViews } from './components/SavedViews/SavedViews'
import { Settings } from './components/Settings/Settings'
import { StoragePathList } from './components/StoragePaths/StoragePathList'
import { TagList } from './components/Tags/TagList'
import { TaskList } from './components/Tasks/TaskList'
import { TrashList } from './components/Trash/TrashList'
import WorkflowsList from './components/Workflows/WorkflowsList'
import { PopupToast } from './components/common/PopupToast'
import { DMS_BASE_COLOR_HSL, SETTINGS_KEYS } from './config/environment'
import { ModuleKey } from './constants/module-keys'
import i18n, { loadLanguage } from './i18n/config'
import { mapBackendToI18nCode } from './i18n/languages'
import { broadcastService } from './services/broadcast.service'
import { openDocumentsService } from './services/open-documents.service'
import { PermissionAction, permissionsService, PermissionType } from './services/permissions.service'
import { settingsService } from './services/settings.service'
import { toastService } from './services/toast.service'
import { sessionSyncService } from './services/session-sync.service'
import { websocketService } from './services/websocket.service'
import { useAppStore } from './store/useAppStore'
import { Toast } from './types'

// Global logout handler - registered outside component to avoid React Strict Mode issues
let globalLogoutHandler: ((data: any) => void) | null = null
let isLogoutHandlerRegistered = false

type PermissionRouteGate = {
  action: PermissionAction
  type: PermissionType
}

interface PermissionRouteProps {
  children: React.ReactNode
  allOf: PermissionRouteGate[]
  redirectTo?: string
}

const PermissionRoute: React.FC<PermissionRouteProps> = ({ children, allOf, redirectTo = '/dashboard' }) => {
  const location = useLocation()
  const hasPermissions = allOf.every((gate) => permissionsService.currentUserCan(gate.action, gate.type))

  if (!hasPermissions) {
    return <Navigate to={redirectTo} replace state={{ from: location, missingPermissions: true }} />
  }

  return <>{children}</>
}

const AppContent: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [isInitialized, setIsInitialized] = useState(false)
  const [popupToast, setPopupToast] = useState<Toast | null>(null)
  const hasInitializedRef = React.useRef(false)
  const prevPathRef = React.useRef(location.pathname)

  useEffect(() => {
    websocketService.setNavigateCallback((path: string) => {
      navigate(path)
    })
    websocketService.setLocationCallback(() => {
      return location.pathname
    })
  }, [navigate, location])

  // Logout subscription - registered globally to avoid React Strict Mode issues
  useEffect(() => {
    // Register only once globally (not per component instance)
    if (isLogoutHandlerRegistered) {
      return
    }

    globalLogoutHandler = (data?: { reason?: string }) => {
      // Update store state
      useAppStore.getState().setAuthenticated(false)
      useAppStore.getState().logout()

      if (!window.location.pathname.startsWith('/login')) {
        const query =
          data?.reason === 'session_replaced'
            ? 'session=replaced'
            : data?.reason === 'license_expired'
              ? 'license=expired'
              : ''
        window.location.replace(query ? `/login?${query}` : '/login')
      }
    }

    broadcastService.subscribe('logout', globalLogoutHandler)

    const handleSessionChanged = () => {
      void sessionSyncService.syncFromServer()
    }

    broadcastService.subscribe('session-changed', handleSessionChanged)

    // Subscribe to login events to connect WebSocket after successful login
    broadcastService.subscribe('login', () => {
      setTimeout(() => {
        if (!window.location.pathname.startsWith('/login')) {
          websocketService.connect().catch((error) => {
            console.error('[App] Failed to connect WebSocket after login:', error)
          })
        }
      }, 500)
    })

    broadcastService.subscribe('feature-flags-updated', (flags) => {
      useAppStore.getState().setFeatureFlags(flags)
    })

    isLogoutHandlerRegistered = true

    // Don't unsubscribe in cleanup - keep it registered globally
    return () => {
      // Keep subscription active
    }
  }, []) // Empty dependency array - register only once

  // Disconnect WebSocket only on unmount (e.g. logout), not on every route change
  useEffect(() => {
    return () => {
      websocketService.disconnect()
    }
  }, [])

  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void sessionSyncService.syncFromServer()
      }
    }
    document.addEventListener('visibilitychange', onVisibilityChange)
    return () => document.removeEventListener('visibilitychange', onVisibilityChange)
  }, [])

  useEffect(() => {
    const root = document.documentElement
    root.style.setProperty('--pngx-primary', `${DMS_BASE_COLOR_HSL.h}, ${DMS_BASE_COLOR_HSL.s}%`)
    root.style.setProperty('--pngx-primary-lightness', `${DMS_BASE_COLOR_HSL.l}%`)
    if (!localStorage.getItem('app_language')) {
      localStorage.setItem('app_language', 'sr')
    }

    // Keep neutral/default theme until org-scoped ui_settings are loaded.
    settingsService.updateAppearanceSettings(undefined, undefined, undefined, false)

    const initializeApp = async () => {
      if (window.location.pathname.startsWith('/login')) {
        setIsInitialized(true)
        return
      }

      try {
        await settingsService.initializeSettings()
        openDocumentsService.setActiveOrganizationId(settingsService.getCurrentOrganizationId())

        const languageCode = settingsService.get(SETTINGS_KEYS.LANGUAGE)
        if (languageCode) {
          const i18nCode = mapBackendToI18nCode(languageCode)
          await loadLanguage(i18nCode)
          localStorage.setItem('app_language', i18nCode)
        }

        await settingsService.updateAppearanceSettings()

        // Connect to WebSocket (async, but don't wait for it)
        websocketService.connect().catch((error) => {
          console.error('[App] Failed to connect WebSocket:', error)
        })
      } catch (error: any) {
        console.error('[App] Error during initialization:', error)
        if (error.response?.status === 401 || error.status === 401) {
          window.location.href = '/login?license=expired'
        }
      } finally {
        setIsInitialized(true)
      }
    }

    const isOnLogin = location.pathname.startsWith('/login')
    const cameFromLogin = prevPathRef.current.startsWith('/login')
    prevPathRef.current = location.pathname

    // Only run init when: not on login AND (first time or just navigated from login)
    if (!isOnLogin && (!hasInitializedRef.current || cameFromLogin)) {
      hasInitializedRef.current = true
      initializeApp()
    } else if (isOnLogin) {
      setIsInitialized(true)
    }

    const unsubscribe = toastService.subscribeToShowToast((toast) => {
      setPopupToast(toast)
    })

    const urlParams = new URLSearchParams(window.location.search)
    const showLocalizedError = async (key: string) => {
      const lng = i18n.language || localStorage.getItem('app_language') || 'sr'
      if (!i18n.hasResourceBundle(lng, 'translation')) {
        await loadLanguage(lng)
      }
      toastService.showError(i18n.t(key))
    }
    if (urlParams.get('license') === 'expired') {
      void showLocalizedError('license_expired_message')
      urlParams.delete('license')
      const newUrl = window.location.pathname + (urlParams.toString() ? '?' + urlParams.toString() : '')
      window.history.replaceState({}, '', newUrl)
    } else if (urlParams.get('session') === 'replaced') {
      void showLocalizedError('session_replaced')
      urlParams.delete('session')
      const newUrl = window.location.pathname + (urlParams.toString() ? '?' + urlParams.toString() : '')
      window.history.replaceState({}, '', newUrl)
    }

    return () => {
      unsubscribe()
    }
  }, [location.pathname])

  if (!isInitialized) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    )
  }
  const handlePopupToastClose = (toast: Toast) => {
    if (toast.id) {
      toastService.onPopupToastDismissed(toast.id)
    }
  }

  return (
    <>
      {popupToast && <PopupToast key={popupToast.id} toast={popupToast} autohide={true} onClose={handlePopupToastClose} />}

      <Routes>
        <Route path="/login" element={<Login />} />

        <Route
          path="/documents/:id/preview"
          element={
            <PrivateRoute>
              <DocumentPreview />
            </PrivateRoute>
          }
        />

        <Route
          path="/"
          element={
            <PrivateRoute>
              <AppFrame />
            </PrivateRoute>
          }
        >
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="documents" element={<DocumentList />} />
          <Route path="documents/:id/:section" element={<DocumentDetail />} />
          <Route path="documents/:id" element={<DocumentDetail />} />
          <Route path="asn/:asn" element={<DocumentASN />} />
          <Route path="view/:id" element={<DocumentList />} />
          <Route path="trash" element={<TrashList />} />

          {/* Manage routes */}
          <Route path="tags" element={<TagList />} />
          <Route path="correspondents" element={<CorrespondentList />} />
          <Route path="documenttypes" element={<DocumentTypeList />} />
          <Route path="workflowstatuses" element={<WorkflowStatusList />} />
          <Route
            path="documentworkflowdefinitions"
            element={
              <PermissionRoute
                allOf={[{ action: PermissionAction.Display, type: PermissionType.DocumentWorkflowDefinition }]}
              >
                <DocumentWorkflowDefinitionList />
              </PermissionRoute>
            }
          />
          <Route
            path="documentworkflowtasks"
            element={
              <PermissionRoute
                allOf={[{ action: PermissionAction.Display, type: PermissionType.DocumentWorkflowTask }]}
              >
                <DocumentWorkflowTaskList />
              </PermissionRoute>
            }
          />
          <Route path="storagepaths" element={<StoragePathList />} />
          <Route path="customfields" element={<CustomFields />} />
          <Route
            path="savedviews"
            element={
              <ProtectedRoute moduleKey={ModuleKey.SavedViews}>
                <PermissionRoute
                  allOf={[
                    { action: PermissionAction.Display, type: PermissionType.SavedView },
                    { action: PermissionAction.View, type: PermissionType.SavedView },
                  ]}
                >
                  <SavedViews />
                </PermissionRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="email"
            element={
              <ProtectedRoute moduleKey={ModuleKey.EmailIntegration}>
                <EmailSettings />
              </ProtectedRoute>
            }
          />
          <Route
            path="workflows"
            element={
              <ProtectedRoute moduleKey={ModuleKey.Workflow}>
                <WorkflowsList />
              </ProtectedRoute>
            }
          />

          {/* Settings */}
          <Route path="settings" element={<Settings />} />
          <Route path="settings/:section" element={<Settings />} />

          {/* API Documentation */}
          <Route path="api-documentation" element={<ApiDocumentation />} />

          {/* Admin routes */}
          <Route path="config" element={<Navigate to="/settings/branding" replace />} />
          <Route path="usersgroups" element={<UsersAndGroups />} />
          <Route path="tasks" element={<TaskList />} />
        </Route>

        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </>
  )
}

const App: React.FC = () => {
  return (
    <Router>
      <AppContent />
    </Router>
  )
}

export default App
